<template>
  <div>
    <div class="alert-box" style="margin-left: 120px">
      <Alert v-if="errors.length" type="danger" :messages="errors" />
    </div>
    <table class="table" v-show="!loading">
      <thead>
        <tr>
          <th style="width:60px; white-space: nowrap;">並び替え</th>
          <th style="width:60px; white-space: nowrap;">表示順</th>
          <th style="width:381px">リスト名</th>
          <th style="width:318px">閲覧権限</th>
          <th style="width:150px">操作</th>
        </tr>
      </thead>
      <tbody v-if="!sortedReviewLists.length">
        <tr>
          <td colspan="100%" class="text-center">
            {{ searchQuery ? '検索結果が見つかりません' : 'リスト名が見つかりません' }}
          </td>
        </tr>
      </tbody>
      <draggable 
        v-else
        :list="draggableList" 
        tag="tbody" 
        :disabled="dragOptions" 
        @end="onDragEnd"
        handle=".drag-handle"
      >
        <ReviewListRowDraggable
          v-for="(list, index) in draggableList"
          v-bind:key="list._id"
          :reviewLists="list"
          :index="index"
          :displayNumber="getAdjustedIndex(index)"
          @error="handleError"
        />
      </draggable>
    </table>
    <Loader v-show="loading" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import Loader from '@/components/utils/Loader.vue';
import ReviewListRowDraggable from './ReviewListRowDraggable.vue';
import Alert from '@/components/utils/Alert.vue';
import { mapActions, mapGetters } from 'vuex';
import ApiService from '@/service/apiService';
import Api from '@/config/api';

export default {
  name: 'ReviewListsWithDragDrop',
  components: { ReviewListRowDraggable, Loader, Alert, draggable },
  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      errors: [],
      isSubmitting: false,
      originalLists: [],
    };
  },
  async mounted() {
    await this.loadData();
  },
  computed: {
    ...mapGetters({
      getCurrentUser: 'getCurrentUser',
      reviewListUsers: 'getReviewListUsers',
      getReviewLists: 'getReviewLists',
    }),
    sortedReviewLists() {
      // Sort by position, then by createdAt
      return [...this.getReviewLists].sort((a, b) => {
        if (a.position !== b.position) {
          return (a.position || 1) - (b.position || 1);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    },
    dragOptions() {
      return Boolean(this.searchQuery.trim()) || this.isSubmitting || this.loading;
    },
    draggableList() {
      // Filter by search query if provided
      let lists = [...this.getReviewLists];
      
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        lists = lists.filter(list => 
          list.title.toLowerCase().includes(query)
        );
      }
      
      return lists;
    },
  },
  watch: {
    searchQuery() {
      // No need to reload, just let computed property handle filtering
    },
  },
  methods: {
    ...mapActions({
      loadReviewListUsers: 'reviewListUsers',
      loadCurrentUser: 'loadCurrentUser',
      loadReviewLists: 'loadReviewLists',
    }),
    async loadData() {
      this.loading = true;
      try {
        if (!this.getCurrentUser) await this.loadCurrentUser();
        if (!this.getReviewLists || !this.getReviewLists.length) {
          await this.loadReviewLists();
        }
        if (this.reviewListUsers && !this.reviewListUsers.length) {
          await this.loadReviewListUsers();
        }
        this.originalLists = [...this.getReviewLists];
      } catch (error) {
        console.error('Error loading data:', error);
        this.errors = ['データの読み込みに失敗しました'];
      } finally {
        this.loading = false;
      }
    },
    handleError(message) {
      this.errors = message ? [message] : [];
    },
    getAdjustedIndex(arrayIndex) {
      // Return the display number based on position
      const item = this.draggableList[arrayIndex];
      return item ? item.position : arrayIndex + 1;
    },
    onDragEnd() {
      // Update positions after drag
      let newLists = [...this.draggableList];
      newLists.forEach((item, index) => {
        item.position = index + 1;
      });
      // Call API to save new order
      this.reOrder();
    },
    async reOrder() {
      this.isSubmitting = true;
      try {
        let reorderedLists = this.draggableList.map((list) => {
          return {
            id: list._id,
            position: list.position,
          };
        });

        let res = await ApiService.put(
          Api.endpoints.UPDATE_REVIEW_LIST_ORDER,
          { listOrder: reorderedLists }
        );

        // Update the store with new positions
        const updatedLists = this.draggableList.map((list, idx) => {
          list.position = idx + 1;
          return list;
        });
        this.$store.commit('setReviewLists', updatedLists);
        this.originalLists = [...updatedLists];

        this.isSubmitting = false;
      } catch (err) {
        // Revert to original order on error
        this.$store.commit('setReviewLists', this.originalLists);
        this.errors = this.parseErrorResponse(err.response);
        if (!this.errors || !this.errors.length) {
          this.errors = ['並び順の更新に失敗しました'];
        }
        this.isSubmitting = false;
        console.log(err);
      }
    },
    parseErrorResponse(response) {
      if (response && response.data) {
        if (response.data.errors && Array.isArray(response.data.errors)) {
          return response.data.errors.map(err => err.message || err);
        } else if (response.data.error) {
          return [response.data.error];
        } else if (response.data.error_description) {
          return [response.data.error_description];
        }
      }
      return ['エラーが発生しました'];
    },
  },
};
</script>

<style scoped>
.table td {
  border-bottom: 1px solid #787878 !important;
  vertical-align: middle;
}
table {
  width: 85%;
  margin: auto;
  position: relative;
  border-bottom: 1px solid #787878;
}
.table thead {
  border-bottom: 1px solid #787878;
}
.table th {
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  vertical-align: middle;
  padding: 0.75rem;
}

/* Drag and drop styles */
tbody tr {
  transition: background-color 0.2s ease;
}

tbody tr:hover {
  background-color: #f8f9fa;
}
</style>
